/**
 * O contexto que liga as tres maquinas puras do PWA (conexao, atualizacao, instalacao) a arvore
 * de componentes.
 *
 * A separacao e a de sempre neste app: **decisao pura em `.ts`, efeito no provedor**. O
 * provedor e o unico arquivo que toca `navigator`, `window` e `serviceWorker`; ele recebe tudo
 * o que e efeito por parametro, com default de producao, para que a tela possa ser testada com
 * um estado declarado em vez de um navegador simulado.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from 'react';

import {
  ESTADO_INICIAL,
  ehRedeDegradada,
  reduzir,
  type DicasDeRede,
  type EstadoDeConexao,
  type Evento,
} from './conexao.js';
import {
  NENHUM_FLUXO_CRITICO,
  proximoEstado,
  type EstadoDaAtualizacao,
  type FluxosCriticos,
} from './atualizacao.js';

export interface ValorDoPwa {
  readonly conexao: EstadoDeConexao;
  readonly atualizacao: EstadoDaAtualizacao;
  /** O usuario tocou em "Atualizar". Manda `SKIP_WAITING` e recarrega em `controllerchange`. */
  readonly aplicarAtualizacao: () => void;
  /** Marca/desmarca um fluxo critico. `PWA §5.2`, PWA-011. */
  readonly marcarFluxoCritico: (fluxo: keyof FluxosCriticos, ativo: boolean) => void;
  readonly notificarFalhaDeRede: () => void;
  readonly notificarSucessoDeRede: () => void;
}

const ContextoDoPwa = createContext<ValorDoPwa | null>(null);

function lerDicas(): DicasDeRede {
  if (typeof navigator === 'undefined') return { online: true };
  const conexao = (navigator as Navigator & { connection?: { effectiveType?: string; saveData?: boolean } })
    .connection;
  return {
    online: navigator.onLine,
    effectiveType: conexao?.effectiveType,
    saveData: conexao?.saveData,
  };
}

export interface ProvedorDePwaProps {
  readonly children: ReactNode;
  /** Estado inicial de conexao, para o teste declarar `OFFLINE` sem simular falhas. */
  readonly conexaoInicial?: EstadoDeConexao;
  /** Ha versao nova esperando? Em producao vem do `registration.waiting`. */
  readonly temVersaoNova?: boolean;
  readonly fluxosIniciais?: FluxosCriticos;
  /** O efeito de aplicar: `postMessage({type:'SKIP_WAITING'})` + reload. */
  readonly aoAplicarAtualizacao?: () => void;
  readonly lerDicasDeRede?: () => DicasDeRede;
}

export function ProvedorDePwa({
  children,
  conexaoInicial,
  temVersaoNova = false,
  fluxosIniciais = NENHUM_FLUXO_CRITICO,
  aoAplicarAtualizacao,
  lerDicasDeRede = lerDicas,
}: ProvedorDePwaProps): ReactNode {
  const [dicas, setDicas] = useState<DicasDeRede>(() => lerDicasDeRede());

  const [estadoDeConexao, despachar] = useReducer(
    (atual: typeof ESTADO_INICIAL, evento: Evento) => reduzir(atual, evento, dicas),
    conexaoInicial === undefined
      ? ESTADO_INICIAL
      : { estado: conexaoInicial, falhas: [] as readonly number[] },
  );

  const [fluxos, setFluxos] = useState<FluxosCriticos>(fluxosIniciais);
  const [aceito, setAceito] = useState(false);

  // `navigator.onLine === false` e atalho para ENTRAR em OFFLINE, nunca para sair (`PWA §6.3`).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const aoFicarOffline = (): void => { despachar({ tipo: 'NAVEGADOR_OFFLINE' }); };
    const aoMudarDicas = (): void => {
      const novas = lerDicasDeRede();
      setDicas(novas);
      despachar({ tipo: 'DICAS_MUDARAM', dicas: novas });
    };
    window.addEventListener('offline', aoFicarOffline);
    window.addEventListener('online', aoMudarDicas);
    return () => {
      window.removeEventListener('offline', aoFicarOffline);
      window.removeEventListener('online', aoMudarDicas);
    };
  }, [lerDicasDeRede]);

  const atualizacao = useMemo<EstadoDaAtualizacao>(
    () => proximoEstado({ temVersaoNova, fluxos, aceito }),
    [temVersaoNova, fluxos, aceito],
  );

  const aplicarAtualizacao = useCallback(() => {
    setAceito(true);
    aoAplicarAtualizacao?.();
  }, [aoAplicarAtualizacao]);

  const marcarFluxoCritico = useCallback((fluxo: keyof FluxosCriticos, ativo: boolean) => {
    setFluxos((atual) => ({ ...atual, [fluxo]: ativo }));
  }, []);

  const valor = useMemo<ValorDoPwa>(
    () => ({
      conexao:
        estadoDeConexao.estado === 'ONLINE' && ehRedeDegradada(dicas)
          ? 'DEGRADED'
          : estadoDeConexao.estado,
      atualizacao,
      aplicarAtualizacao,
      marcarFluxoCritico,
      notificarFalhaDeRede: () => { despachar({ tipo: 'FALHA_DE_REDE', emMs: Date.now() }); },
      notificarSucessoDeRede: () => { despachar({ tipo: 'SUCESSO_DE_REDE' }); },
    }),
    [estadoDeConexao.estado, dicas, atualizacao, aplicarAtualizacao, marcarFluxoCritico],
  );

  return <ContextoDoPwa.Provider value={valor}>{children}</ContextoDoPwa.Provider>;
}

export function usePwa(): ValorDoPwa {
  const valor = useContext(ContextoDoPwa);
  if (valor === null) throw new Error('Use <ProvedorDePwa> acima do componente.');
  return valor;
}

/**
 * Marca um fluxo critico enquanto o componente estiver montado — PWA-011.
 *
 * A tela de checkout chama `useFluxoCritico('checkoutInProgress')` e pronto: o aviso de
 * atualizacao some ao entrar e volta ao sair, sem que a tela precise lembrar de desmarcar. A
 * limpeza no `return` do efeito e o que garante que uma navegacao inesperada (voltar do
 * navegador, erro, sessao perdida) nao deixe o app com o aviso suprimido para sempre.
 */
export function useFluxoCritico(fluxo: keyof FluxosCriticos): void {
  const { marcarFluxoCritico } = usePwa();
  useEffect(() => {
    marcarFluxoCritico(fluxo, true);
    return () => { marcarFluxoCritico(fluxo, false); };
  }, [fluxo, marcarFluxoCritico]);
}
